import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AppColors } from '../theme/colors';

/**
 * 無料実証の期間を知らせる表示。
 *
 *   無料利用期間：12月31日まで（あと23日）
 *
 * 終了日は常時出しておき、終了の1週間前からは「あと○日」の部分だけを
 * 赤文字にして気づけるようにする（2026-10-02 レフリア様と確定）。
 *
 * 管理画面にだけ置く。投稿する社員の方には操作に関係がないため出さない。
 *
 * 押すと会社管理へ移る。有料への切り替えはそこにあるため、
 * どの画面からでもたどり着けるようにしておく。
 */

/** 強調に切り替える残り日数 */
export const HIGHLIGHT_DAYS = 7;

interface FreeTrialPeriodLabelProps {
  /** 無料期間の終了日。Firestore の Timestamp でも Date でも受け取る */
  endDate: { toDate?: () => Date } | Date | null | undefined;
  /** 今日。テストから差し替えられるようにしておく */
  now?: Date;
  /**
   * 短い形（「無料 あと○日」）で出す。スマホのヘッダーは幅が狭く、
   * 長い形だと会社名や「管理画面」を押し出して重なってしまうため
   */
  compact?: boolean;
}

export const FreeTrialPeriodLabel: React.FC<FreeTrialPeriodLabelProps> = ({ endDate, now, compact = false }) => {
  const navigate = useNavigate();
  const end = toDate(endDate);
  if (!end) return null;

  const today = now ?? new Date();
  const daysRemaining = countDaysRemaining(end, today);

  // 終了日を過ぎている場合は出さない。停止の案内に切り替わるため（#21）
  if (daysRemaining < 0) return null;

  const isNearEnd = daysRemaining <= HIGHLIGHT_DAYS;
  const remainingText = daysRemaining === 0 ? '本日まで' : `あと${daysRemaining}日`;

  return (
    <Tooltip title="お支払いのお手続きはこちら">
      <Box
        component="button"
        type="button"
        onClick={() => {
          // 期間中は請求の手前にあり、開いただけでは見えない。
          // 会社管理へ移ったうえで、切り替えのカードまで送る。
          navigate('/admin/company-management#free-trial-upgrade');
        }}
        sx={{
          display: 'inline-flex',
          alignItems: 'baseline',
          gap: 0.5,
          px: compact ? 0.75 : 1.25,
          py: 0.4,
          flexShrink: 0,
          borderRadius: 1.5,
          // ヘッダーの緑に乗せるため、白地にして浮かせる
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
          whiteSpace: 'nowrap',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
        }}
      >
        {compact ? (
          <>
            <Typography component="span" sx={{ fontSize: 12, color: AppColors.textSecondary }}>
              無料
            </Typography>
            <Typography
              component="span"
              aria-label={`無料利用期間：${formatDate(end)}まで（${remainingText}）`}
              sx={{
                fontSize: 12,
                fontWeight: isNearEnd ? 700 : 600,
                color: isNearEnd ? AppColors.error : AppColors.textPrimary,
              }}
            >
              {remainingText}
            </Typography>
          </>
        ) : (
          <>
            <Typography component="span" sx={{ fontSize: 13, color: AppColors.textSecondary }}>
              無料利用期間：
            </Typography>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: AppColors.textPrimary }}>
              {formatDate(end)}まで
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: 13,
                fontWeight: isNearEnd ? 700 : 400,
                color: isNearEnd ? AppColors.error : AppColors.textSecondary,
              }}
            >
              （{remainingText}）
            </Typography>
          </>
        )}
      </Box>
    </Tooltip>
  );
};

/** Firestore の Timestamp と Date のどちらでも受け取れるようにする */
function toDate(value: { toDate?: () => Date } | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  return null;
}

/** 残り日数。JSTの日付どうしで引く。終了日当日は0 */
function countDaysRemaining(endDate: Date, now: Date): number {
  const JST_OFFSET = 9 * 60 * 60 * 1000;
  const dateOnly = (d: Date) => {
    const jst = new Date(d.getTime() + JST_OFFSET);
    return Date.UTC(jst.getUTCFullYear(), jst.getUTCMonth(), jst.getUTCDate());
  };
  return Math.round((dateOnly(endDate) - dateOnly(now)) / (24 * 60 * 60 * 1000));
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric' });
}
